import { useEffect, useMemo, useState } from 'react';
import { findEntry, listLibrary } from '@/api/library';
import { useAuth } from '@/context/AuthContext';
import { useCatalog } from '@/context/CatalogContext';
import { basePrice, calcTotals, languageCharges, unitPrice } from '@/utils/pricing';

/**
 * Checkout's line for renewing one book, in the same shape useCartItems returns.
 *
 * A renewal is not a cart line: it is bought from the purchase history, for one
 * book, and renews exactly what the reader holds — so those languages come from
 * their purchase on the platform, never from a picker. `addLanguages` are any the
 * reader chose to ADD for the new term ("renew with Marathi"), from the book's
 * page. The price shown here is a preview; the server prices the renewal from the
 * same purchase before taking any money.
 *
 * `bookId` is the version on sale; the reader's purchase may hold an earlier one,
 * so it is matched by lineage (findEntry).
 *
 * `entitlement` is undefined while it is being looked up, null when the reader
 * holds no purchase of this book (or it could not be read).
 */
export function useRenewalItems({ bookId, addLanguages = [], discount = 0 }) {
  const { getProduct, status } = useCatalog();
  const { isSignedIn } = useAuth();
  const [entitlement, setEntitlement] = useState(undefined);

  useEffect(() => {
    if (!bookId || !isSignedIn) {
      setEntitlement(undefined);
      return undefined;
    }
    const controller = new AbortController();
    setEntitlement(undefined);
    listLibrary({ signal: controller.signal })
      .then((entries) => setEntitlement(entries))
      .catch((err) => {
        if (err.name !== 'AbortError') setEntitlement(null);
      });
    return () => controller.abort();
  }, [bookId, isSignedIn]);

  const added = addLanguages.join(',');
  return useMemo(() => {
    const isCatalogReady = status === 'ready';
    const product = bookId ? getProduct(bookId) : null;
    // `entitlement` holds the whole library until both it and the catalogue are
    // in, because the match needs the product's lineage.
    const entry = Array.isArray(entitlement) ? findEntry(entitlement, product ?? { id: bookId }) : entitlement;
    const base = {
      isCatalogReady,
      entitlement: entry,
      /** True once both the catalogue and the reader's purchase have answered. */
      isReady: isCatalogReady && entitlement !== undefined,
      requiresDelivery: false,
      isMultiCurrency: false,
    };

    if (!product || !entry) {
      return {
        ...base,
        items: [],
        currency: product?.currency ?? null,
        totals: calcTotals({ items: [], discount }),
      };
    }

    const held = entry.additional_languages ?? [];
    const extras = added
      ? added.split(',').filter((l) => l && !held.includes(l) && (product.additional_languages ?? []).includes(l))
      : [];
    const languages = [...held, ...extras];
    const price = unitPrice(product, languages);
    const item = {
      ...product,
      key: `renew:${bookId}`,
      kind: 'renewal',
      languages,
      price,
      basePrice: basePrice(product),
      languageCharges: languageCharges(product, languages),
      qty: 1,
      lineTotal: price,
    };
    return {
      ...base,
      items: [item],
      currency: product.currency,
      totals: calcTotals({ items: [item], discount }),
    };
  }, [bookId, getProduct, status, entitlement, added, discount]);
}
